import { useEffect, useRef } from "react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QuickBasicsData, INDUSTRIES, TIMEZONES } from "@/domain/intake";
import {
  useEmailDomainValidation,
  EmailDomainStatus,
} from "@/hooks/use-email-domain-validation";
import { CloudflareTurnstile } from "@/components/auth/CloudflareTurnstile";

interface QuickBasicsStepProps {
  data: QuickBasicsData;
  onChange: (data: QuickBasicsData) => void;
  onNext: () => void;
}

function EmailStatusIndicator({ status }: { status: EmailDomainStatus }) {
  if (status === "verifying") {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground/60" />
        <span className="text-xs text-muted-foreground/60">Verifying</span>
      </div>
    );
  }
  if (status === "valid") {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <CheckCircle2 className="h-4 w-4 text-primary/80" />
      </div>
    );
  }
  if (
    status === "disposable" ||
    status === "personal" ||
    status === "error"
  ) {
    return (
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <XCircle className="h-4 w-4 text-destructive" />
      </div>
    );
  }
  return null;
}

function emailInputClassName(
  status: EmailDomainStatus,
  hasError: boolean,
): string {
  const base = "pr-28";
  if (hasError) return `${base} border-destructive/50`;
  if (status === "valid") return `${base} border-primary/30`;
  if (status === "verifying") return `${base} border-muted-foreground/20`;
  return base;
}

export function QuickBasicsStep({
  data,
  onChange,
  onNext,
}: QuickBasicsStepProps) {
  const {
    status: emailStatus,
    errorMessage: emailError,
    isBlocking: emailIsBlocking,
    validateEmail,
    sanitizeEmail,
  } = useEmailDomainValidation();

  const turnstileVerified = useRef(false);

  useEffect(() => {
    if (data.email) {
      validateEmail(data.email);
    }
  }, []);

  const handleFieldChange = (field: keyof QuickBasicsData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const handleEmailChange = (value: string) => {
    const sanitized = sanitizeEmail(value);
    onChange({ ...data, email: sanitized });
    validateEmail(sanitized);
  };

  const isValid =
    data.name.trim() !== "" &&
    !emailIsBlocking &&
    data.discussionTopic.trim() !== "" &&
    data.industryId !== "" &&
    data.timezone !== "";

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8">
        <h2 className="font-display text-2xl font-bold mb-2">
          Tell us about yourself
        </h2>
        <p className="text-muted-foreground mb-6">
          A few quick details so we can make the most of your discovery call.
        </p>

        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="John Smith"
                value={data.name}
                onChange={(e) => handleFieldChange("name", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company Name</Label>
              <Input
                id="company"
                placeholder="Acme Inc (optional)"
                value={data.companyName}
                onChange={(e) =>
                  handleFieldChange("companyName", e.target.value)
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Business Email *</Label>
            <div className="relative">
              <Input
                id="email"
                type="text"
                autoComplete="off"
                placeholder="john@acme.com"
                value={data.email}
                onChange={(e) => handleEmailChange(e.target.value)}
                className={emailInputClassName(emailStatus, !!emailError)}
              />
              <EmailStatusIndicator status={emailStatus} />
            </div>
            {emailError && (
              <p className="text-sm text-destructive">{emailError}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="topic">What would you like to discuss? *</Label>
            <Textarea
              id="topic"
              placeholder="E.g. I have an idea for an AI-powered desktop app and need help scoping and building it..."
              rows={3}
              maxLength={500}
              value={data.discussionTopic}
              onChange={(e) =>
                handleFieldChange("discussionTopic", e.target.value)
              }
            />
          </div>

          <div className="space-y-2">
            <Label>Industry *</Label>
            <Select
              value={data.industryId}
              onValueChange={(v) => handleFieldChange("industryId", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((industry) => (
                  <SelectItem key={industry.id} value={industry.id}>
                    {industry.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Time Zone *</Label>
            <Select
              value={data.timezone}
              onValueChange={(v) => handleFieldChange("timezone", v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your time zone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.id} value={tz.id}>
                    {tz.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sr-only" aria-hidden="true">
            <CloudflareTurnstile
              onVerify={() => {
                turnstileVerified.current = true;
              }}
              size="compact"
            />
          </div>

          <div className="pt-4 flex justify-end">
            <Button onClick={onNext} disabled={!isValid} size="lg">
              Next
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
